/**
 * QuotaBanner — inline soft-lock notice for a metric that is at or over limit.
 * Mirrors the Rails `shared/_quota_lock` partial.
 */

import { h } from 'preact'
import { normalizeQuota, quotaUpgradePath } from './parseQuotaError.js'
import { t as defaultT, metricLabel } from './quota-i18n.js'
import { QuotaMeter } from './QuotaMeter.jsx'
import { QuotaCtas } from './QuotaCtas.jsx'

export function QuotaBanner({
  quota,
  t = defaultT,
  title,
  help,
  format,
  compact = false,
  showMeter = true,
  // Hide the banner while usage is below this fraction of the limit (0 = always show).
  threshold = 0,
  onDismiss,
  className = ''
}) {
  const data = normalizeQuota(quota)
  if (!data) return null
  if (threshold > 0 && !data.blocking && (data.ratio == null || data.ratio < threshold)) return null

  const { metric, blocking, exceeded, upgrades } = data
  const label = metricLabel(metric, t)
  const heading = title ?? (exceeded || blocking
    ? t('quotas.limit_reached', { metric: label })
    : t('quotas.approaching_limit', { metric: label }))
  const variant = blocking || exceeded ? 'danger' : 'warning'
  const upgradePath = upgrades[0]?.path ?? quotaUpgradePath(null)
  const helpText = help ?? t('quotas.soft_lock_help')

  if (compact) {
    return (
      <a
        href={upgradePath}
        class={`quota-lock-link text-decoration-none ${className}`}
        title={heading}
      >
        <span class={`badge text-bg-${variant}-subtle text-${variant} border border-${variant}-subtle d-inline-flex align-items-center gap-1`}>
          <i class="bi bi-speedometer2" aria-hidden="true" />
          <span class="visually-hidden">{heading}</span>
        </span>
      </a>
    )
  }

  return (
    <div class={`quota-lock alert alert-${variant} d-flex align-items-start gap-2 py-2 mb-0 ${className}`} role="status">
      <i class="bi bi-speedometer2 flex-shrink-0 mt-1" aria-hidden="true" />
      <div class="flex-grow-1">
        <div class="fw-semibold">{heading}</div>
        {showMeter && <QuotaMeter quota={data} t={t} format={format} label="" className="my-2" />}
        <div class="small text-muted">{helpText}</div>
        <div class="mt-2">
          <QuotaCtas upgrades={upgrades} t={t} variant={variant} size="sm" />
        </div>
      </div>
      {onDismiss && (
        <button
          type="button"
          class="btn-close flex-shrink-0"
          aria-label={t('quotas.dismiss')}
          onClick={onDismiss}
        />
      )}
    </div>
  )
}

export default QuotaBanner
